import type { OrderDetails } from '@/entities/order/model'
import { getRestricted } from '@/entities/order/lib'

/**
 * Determines the display value for restricted items based on the order's hsFields
 *
 * Business rules:
 * - If any value greater than 0 is received, display "Yes"
 * - If received 0, display "No"
 * - If the Restricted property is missing, display "No"
 * - If value is "Restricted" (Coles format), display "Yes"
 *
 * @param hsFields - The hsFields object from order details
 * @returns "Yes" or "No" based on the restricted items value
 */
export function getRestrictedItemsDisplay(hsFields?: OrderDetails['hsFields']): string {
  if (!hsFields) {
    return 'No'
  }

  const restrictedValue = getRestricted(hsFields)

  if (restrictedValue === undefined || restrictedValue === null) {
    return 'No'
  }

  // If the value is an array, return "No"
  if (Array.isArray(restrictedValue)) {
    return 'No'
  }

  // Convert to string and then to number for comparison
  const restrictedString = String(restrictedValue).trim()

  // Handle empty string case
  if (restrictedString === '') {
    return 'No'
  }

  // Handle explicit "Yes"/"No" string values
  if (restrictedString.toLowerCase() === 'yes') {
    return 'Yes'
  }

  if (restrictedString.toLowerCase() === 'no') {
    return 'No'
  }

  // Handle Coles "Restricted" string value (equivalent to restricted items present)
  if (restrictedString.toLowerCase() === 'restricted') {
    return 'Yes'
  }

  // Try to parse as number
  const restrictedNumber = Number.parseFloat(restrictedString)

  // If it's not a valid number, default to "No"
  if (Number.isNaN(restrictedNumber)) {
    return 'No'
  }

  // If any value greater than 0, display "Yes"
  // If 0, display "No"
  return restrictedNumber > 0 ? 'Yes' : 'No'
}
